import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fieldCls = "w-full bg-cream border border-charred/20 rounded-md px-4 py-3 text-charred placeholder:text-charred/40 focus:outline-none focus:ring-2 focus:ring-terracotta/40";

function Shell({ subtitle, children, testid }) {
  return (
    <div className="min-h-screen bg-cream grid place-items-center px-6">
      <div className="w-full max-w-sm bg-sand border border-charred/10 rounded-lg p-8" data-testid={testid}>
        <p className="font-serif text-3xl text-charred">SAPORÉ</p>
        <p className="mt-1 text-xs uppercase tracking-[0.25em] text-terracotta">{subtitle}</p>
        {children}
      </div>
    </div>
  );
}

function SetupForm({ onLogin, subtitle }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Le due password non coincidono");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/setup", { password });
      onLogin(data.user);
      toast.success("Password amministratore impostata. Benvenuto!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Configurazione non riuscita"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell subtitle={subtitle} testid="setup-form">
      <div className="mt-5 flex items-start gap-3 bg-olive/10 border border-olive/30 rounded-md p-3">
        <ShieldCheck size={18} className="text-olive-dark shrink-0 mt-0.5" />
        <p className="text-xs text-charred/70 leading-relaxed">
          Prima configurazione: imposta la tua password amministratore personale. Questa schermata sarà disponibile una sola volta.
        </p>
      </div>
      <form onSubmit={submit} className="mt-5 space-y-4">
        <input type="password" required minLength={8} placeholder="Password personale (min. 8 caratteri)" value={password} onChange={(e) => setPassword(e.target.value)} className={fieldCls} data-testid="setup-password-input" autoComplete="new-password" />
        <input type="password" required minLength={8} placeholder="Ripeti la password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={fieldCls} data-testid="setup-confirm-input" autoComplete="new-password" />
        <button type="submit" disabled={loading} data-testid="setup-submit-btn"
          className="w-full rounded-full bg-terracotta text-cream py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-60">
          {loading ? "Salvataggio…" : "Imposta la password"}
        </button>
      </form>
    </Shell>
  );
}

function LoginForm({ onLogin, subtitle }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { password });
      onLogin(data.user);
      toast.success("Bentornato!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Accesso non riuscito"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell subtitle={subtitle} testid="login-form">
      <div className="mt-5 flex items-center gap-3 text-charred/50">
        <Lock size={16} />
        <p className="text-xs">Area riservata al titolare</p>
      </div>
      <form onSubmit={submit} className="mt-4 space-y-4">
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={fieldCls} data-testid="login-password-input" autoComplete="current-password" autoFocus />
        <button type="submit" disabled={loading} data-testid="login-btn"
          className="w-full rounded-full bg-terracotta text-cream py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors disabled:opacity-60">
          {loading ? "Accesso…" : "Accedi"}
        </button>
      </form>
    </Shell>
  );
}

export default function AuthGate({ subtitle, onLogin }) {
  const [needsSetup, setNeedsSetup] = useState(null);

  useEffect(() => {
    api.get("/auth/setup-status")
      .then((r) => setNeedsSetup(r.data.needsSetup))
      .catch(() => setNeedsSetup(false));
  }, []);

  if (needsSetup === null) {
    return <div className="min-h-screen bg-cream grid place-items-center text-charred/50">Caricamento…</div>;
  }
  return needsSetup
    ? <SetupForm subtitle={subtitle} onLogin={onLogin} />
    : <LoginForm subtitle={subtitle} onLogin={onLogin} />;
}
